import { Link } from "react-router-dom";
import {
  CalendarHeart,
  Hand,
  MailOpen,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { Wordmark } from "../components/Brand";

export function Landing() {
  return (
    <div className="min-h-full bg-bg">
      <header className="border-b border-border bg-surface/80 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-5xl px-5 h-14 flex items-center justify-between">
          <Wordmark />
          <Link to="/signin">
            <Button variant="secondary">Sign in</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 opacity-60"
          style={{
            background:
              "radial-gradient(60% 60% at 30% 20%, rgba(37,99,235,0.10), transparent 60%), radial-gradient(50% 50% at 80% 30%, rgba(245,158,11,0.10), transparent 60%)",
          }}
        />
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-20 fade-in">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-soft text-primary text-xs font-semibold border border-primary/15">
                <Sparkles className="h-3.5 w-3.5" /> Family-first care coordination
              </div>
              <h1 className="mt-6 text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
                Care for the people you love,<br className="hidden sm:block" /> together as a family.
              </h1>
              <p className="mt-5 text-text-muted text-lg leading-relaxed">
                Post a doctor visit. Everyone gets notified. One person accepts. No more frantic group
                texts, missed appointments, or "wait, was that today?" moments.
              </p>
              <div className="mt-8 flex items-center justify-center md:justify-start gap-3">
                <Link to="/signin">
                  <Button size="lg">
                    Get started <span aria-hidden>→</span>
                  </Button>
                </Link>
                <a href="#how" className="text-sm text-text-muted hover:text-text">
                  How it works
                </a>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-border shadow-[var(--shadow-lift)] bg-surface">
              <img
                src="/hero.png"
                alt="Three generations of a family reviewing an appointment together at the kitchen table"
                className="block w-full h-auto"
                loading="eager"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-bg-accent border-y border-border">
        <div className="mx-auto max-w-5xl px-5 py-14 sm:py-20">
          <h2 className="text-center text-2xl sm:text-3xl font-semibold tracking-tight">
            How a visit gets covered
          </h2>
          <p className="mt-3 text-center text-text-muted max-w-xl mx-auto">
            Three steps. No app to download for whoever can't make the appointment — they just reply
            to email.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
            <Step
              n={1}
              icon={<CalendarHeart className="h-6 w-6 text-primary" />}
              title="An admin posts the visit"
              body="Doctor, date, location, ride needed. One form."
            />
            <Step
              n={2}
              icon={<MailOpen className="h-6 w-6 text-primary" />}
              title="The whole family is emailed"
              body="A single tap accepts. The first to claim it covers."
            />
            <Step
              n={3}
              icon={<Hand className="h-6 w-6 text-primary" />}
              title="Someone else can assist"
              body="Want to come along? Tap “Assist.” The acceptor isn't alone."
            />
          </div>
        </div>
      </section>

      {/* Why it works */}
      <section className="mx-auto max-w-5xl px-5 py-14 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Feature
            icon={<Users className="h-5 w-5 text-primary" />}
            title="Built for distributed families"
            body="Members spread across cities? Everyone sees the same plan, in their own time zone."
          />
          <Feature
            icon={<ShieldCheck className="h-5 w-5 text-primary" />}
            title="Private by default"
            body="Magic-link sign-in. No passwords. Each family's circle is private — only the people you invite can see it."
          />
          <Feature
            icon={<Sparkles className="h-5 w-5 text-primary" />}
            title="Quiet when it should be"
            body="Decline-only-to-admins, single urgent ping at 24 hours, no group-chat noise."
          />
        </div>
      </section>

      {/* For your family */}
      <section className="bg-bg-accent border-y border-border">
        <div className="mx-auto max-w-3xl px-5 py-14 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-soft text-primary text-xs font-semibold border border-primary/15">
            <Sparkles className="h-3.5 w-3.5" /> Free, private, ad-free
          </div>
          <h2 className="mt-5 text-2xl sm:text-3xl font-semibold tracking-tight">
            Want a Carol Care circle for your family?
          </h2>
          <p className="mt-4 text-text-muted max-w-xl mx-auto leading-relaxed">
            Each family's circle is fully private — your visits, providers, and members are only visible to the people <em>you</em> invite. Free for any family, no ads, no upsells.
          </p>
          <div className="mt-7">
            <Link to="/request-access">
              <Button size="lg">Get your family started</Button>
            </Link>
          </div>
          <p className="mt-3 text-xs text-text-faint">
            We get back within a day or two. Already added? <Link to="/signin" className="text-primary hover:underline">Sign in</Link>.
          </p>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="mx-auto max-w-5xl px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-text-muted">
          <Wordmark size="sm" />
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/privacy" className="hover:text-text">Privacy</Link>
            <Link to="/terms" className="hover:text-text">Terms</Link>
            <Link to="/support" className="hover:text-text">Buy John a coffee</Link>
            <a href="mailto:support@carolcare.io" className="hover:text-text">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Step({
  n,
  icon,
  title,
  body,
}: {
  n: number;
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="bg-surface border border-border rounded-xl p-5 shadow-[var(--shadow-card)]">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-primary-soft flex items-center justify-center">
          {icon}
        </div>
        <div className="text-xs font-semibold uppercase tracking-wider text-text-faint">
          Step {n}
        </div>
      </div>
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-text-muted leading-relaxed">{body}</p>
    </div>
  );
}

function Feature({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex gap-3">
      <div className="h-9 w-9 rounded-lg bg-primary-soft flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="font-semibold">{title}</h3>
        <p className="mt-1 text-sm text-text-muted leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
